import { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import {
  categoriesService,
  subcategoriesService,
  articlesService,
  Article as SupabaseArticle,
  Category,
  Subcategory,
} from "@/lib/supabase";

export interface CategoryInfo {
  id: string;
  name: string;
  slug: string;
}

export interface SubCategoryInfo {
  id: string;
  name: string;
  category: string;
  slug: string;
}

export interface Article {
  id: string;
  title: string;
  excerpt: string;
  category: string;
  subCategory?: string;
  categoryId: string;
  subCategoryId?: string;
  author: string;
  date: string;
  readTime: string;
  imageUrl: string;
}

// Hàm chuyển đổi từ dữ liệu Supabase sang định dạng UI
const mapSupabaseArticleToUi = (
  article: SupabaseArticle,
  categories: Category[],
  subcategories: Subcategory[]
): Article => {
  const category = categories.find((c) => c.id === article.category_id);
  const subcategory = article.subcategory_id
    ? subcategories.find((sc) => sc.id === article.subcategory_id)
    : undefined;

  return {
    id: article.id,
    title: article.title,
    excerpt: article.excerpt,
    category: category?.name || "Không xác định",
    subCategory: subcategory?.name,
    categoryId: article.category_id,
    subCategoryId: article.subcategory_id,
    author: article.author,
    date: new Date(article.date).toLocaleDateString("vi-VN", {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    readTime: article.read_time,
    imageUrl: article.image_url,
  };
};

// Main categories mapping (đổi từ slug sang tên hiển thị)
export const mapCategorySlugToName = (
  slug: string,
  categories: CategoryInfo[]
): string => {
  const category = categories.find((c) => c.slug === slug);
  return category?.name || slug;
};

export function useArticles() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [selectedSubCategories, setSelectedSubCategories] = useState<string[]>(
    []
  );
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);

  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [totalArticles, setTotalArticles] = useState<number>(0);
  const [categories, setCategories] = useState<CategoryInfo[]>([]);
  const [allSubcategories, setAllSubcategories] = useState<SubCategoryInfo[]>(
    []
  );
  const [categoryDisplayNames, setCategoryDisplayNames] = useState<
    Record<string, string>
  >({});

  // Lưu trữ dữ liệu gốc từ API để dùng khi lọc
  const [categoriesData, setCategoriesData] = useState<Category[]>([]);
  const [subcategoriesData, setSubcategoriesData] = useState<Subcategory[]>([]);

  // Pagination settings
  const [hasMore, setHasMore] = useState(true);
  const [page, setPage] = useState(1);
  const itemsPerPage = 5;

  // Tải dữ liệu từ Supabase khi component mount
  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      try {
        // Lấy danh sách categories
        const categoriesData = await categoriesService.getAll();
        setCategoriesData(categoriesData);
        const mappedCategories: CategoryInfo[] = categoriesData.map((cat) => ({
          id: cat.id,
          name: cat.name,
          slug: cat.slug,
        }));
        setCategories(mappedCategories);
        console.log("Categories data loaded:", categoriesData);

        // Tạo mapping từ slug đến tên hiển thị
        const displayNames: Record<string, string> = {};
        categoriesData.forEach((cat) => {
          displayNames[cat.slug] = cat.name;
        });
        setCategoryDisplayNames(displayNames);

        // Lấy danh sách subcategories
        const subcategoriesData = await subcategoriesService.getAll();
        setSubcategoriesData(subcategoriesData);
        const mappedSubcategories: SubCategoryInfo[] = subcategoriesData.map(
          (subcat) => {
            const parentCategory = categoriesData.find(
              (c) => c.id === subcat.category_id
            );
            return {
              id: subcat.id,
              name: subcat.name,
              category: parentCategory?.slug || "",
              slug: subcat.slug,
            };
          }
        );
        setAllSubcategories(mappedSubcategories);

        // Lấy tổng số bài viết và bài viết đầu tiên với filtering
        const { data: articlesData, count: totalCount } =
          await articlesService.getArticlesFiltered({
            page: 1,
            pageSize: itemsPerPage,
          });

        // Tạo mảng Category và Subcategory cho hàm mapSupabaseArticleToUi
        const categoriesArray: Category[] = categoriesData;
        const subcategoriesArray: Subcategory[] = subcategoriesData;

        const mappedArticles = articlesData.map((article) =>
          mapSupabaseArticleToUi(article, categoriesArray, subcategoriesArray)
        );

        setArticles(mappedArticles);
        setTotalArticles(totalCount);

        // Kiểm tra xem còn bài viết để tải thêm không
        setHasMore(totalCount > mappedArticles.length);

        setLoading(false);
      } catch (err) {
        console.error("Lỗi khi tải dữ liệu từ Supabase:", err);
        setError("Đã xảy ra lỗi khi tải dữ liệu. Vui lòng thử lại sau.");
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Sync URL parameters with state
  useEffect(() => {
    const categoryParam = searchParams.get("category") || "";
    const subCategoryParam = searchParams.get("sub_category") || "";

    console.log("DEBUG - URL params changed:", {
      categoryParam,
      subCategoryParam,
    });

    if (categoryParam) {
      const categorySlugs = categoryParam.split(",");
      console.log("DEBUG - Setting categories from URL:", categorySlugs);
      setSelectedCategories(categorySlugs);
    } else {
      setSelectedCategories([]);
    }

    if (subCategoryParam) {
      const subCategorySlugs = subCategoryParam.split(",");
      console.log("DEBUG - Setting subcategories from URL:", subCategorySlugs);
      setSelectedSubCategories(subCategorySlugs);
    } else {
      setSelectedSubCategories([]);
    }
  }, [searchParams]);

  // Get all available subcategories based on selected categories
  const getAvailableSubcategories = (): SubCategoryInfo[] => {
    // Kiểm tra nếu dữ liệu chưa được tải
    if (categoriesData.length === 0 || subcategoriesData.length === 0) {
      return [];
    }

    if (selectedCategories.length === 0) {
      // If no categories selected, all subcategories are available
      return allSubcategories;
    } else {
      // Only show subcategories for selected categories
      // Lấy ID của các category được chọn
      const selectedCategoryIds = convertCategorySlugsToIds(selectedCategories);

      // Lọc subcategory thuộc các category được chọn
      return allSubcategories.filter((subcat) => {
        // Tìm subcategory gốc để lấy category_id
        const originalSubcat = subcategoriesData.find(
          (sc) => sc.id === subcat.id
        );
        if (!originalSubcat) return false;

        return selectedCategoryIds.includes(originalSubcat.category_id);
      });
    }
  };

  // Thêm hàm kiểm tra debug
  const debugSelectedCategories = () => {
    console.log("Debug selected categories:", {
      selectedCategories,
      availableCategories: categories.map((c) => ({
        id: c.id,
        slug: c.slug,
        name: c.name,
      })),
      rawCategories: categoriesData.map((c) => ({
        id: c.id,
        slug: c.slug,
        name: c.name,
      })),
      matchingArticles: articles.map((a) => ({
        id: a.id,
        title: a.title,
        categoryId: a.categoryId,
      })),
    });
  };

  // Gọi debug khi thay đổi categories
  useEffect(() => {
    if (categories.length > 0 && categoriesData.length > 0) {
      debugSelectedCategories();
    }
  }, [selectedCategories, categories, categoriesData, articles]);

  // Thêm hàm tiện ích
  const convertCategorySlugsToIds = (slugs: string[]): string[] => {
    if (slugs.length === 0) return [];

    console.log("DEBUG - convertCategorySlugsToIds input:", slugs);

    // Log danh sách categories có sẵn
    console.log(
      "DEBUG - Categories available:",
      categoriesData.map((c) => ({
        id: c.id,
        slug: c.slug,
        name: c.name,
      }))
    );

    const result = slugs
      .map((slug) => {
        const cat = categoriesData.find((c) => c.slug === slug);
        console.log(
          `DEBUG - Looking for slug "${slug}":`,
          cat ? `Found: ${cat.id} (${cat.name})` : "Not found"
        );
        return cat ? cat.id : null;
      })
      .filter((id): id is string => id !== null);

    console.log("DEBUG - convertCategorySlugsToIds result:", result);
    return result;
  };

  const convertSubcategorySlugsToIds = (slugs: string[]): string[] => {
    if (slugs.length === 0) return [];

    return slugs
      .map((slug) => {
        const subcat = subcategoriesData.find((sc) => sc.slug === slug);
        return subcat ? subcat.id : null;
      })
      .filter((id): id is string => id !== null);
  };

  // Reset bài viết và thực hiện tìm kiếm mới khi có thay đổi về bộ lọc
  useEffect(() => {
    const applyFilters = async () => {
      if (loading) return;

      setLoading(true);
      // Reset lại trang và bài viết
      setPage(1);
      setArticles([]);

      // Log debug information
      console.log("Applying filters:", {
        selectedCategories,
        selectedSubCategories,
        searchQuery,
        categoriesMapping: categoriesData.map((c) => ({
          id: c.id,
          slug: c.slug,
          name: c.name,
        })),
        subcategoriesMapping: subcategoriesData.map((sc) => ({
          id: sc.id,
          slug: sc.slug,
          category_id: sc.category_id,
        })),
      });

      try {
        // Chuyển đổi slug thành ID
        const selectedCategoryIds =
          convertCategorySlugsToIds(selectedCategories);
        const selectedSubcategoryIds = convertSubcategorySlugsToIds(
          selectedSubCategories
        );

        // Log chi tiết để debug
        console.log("DEBUG - Chi tiết filter params:", {
          selectedCategories, // Slugs được chọn từ UI
          selectedCategoryIds, // IDs sau khi chuyển đổi
          categoriesData: categoriesData.map((c) => ({
            id: c.id,
            slug: c.slug,
            name: c.name,
          })),
        });

        // Lấy dữ liệu đã lọc từ API
        const { data: filteredArticles, count: totalFilteredCount } =
          await articlesService.getArticlesFiltered({
            page: 1,
            pageSize: itemsPerPage,
            categoryIds: selectedCategoryIds,
            subcategoryIds: selectedSubcategoryIds,
            searchQuery: searchQuery,
          });

        // Log chi tiết bài viết trả về để kiểm tra
        console.log(
          "DEBUG - Kết quả articles filter:",
          filteredArticles.map((a) => ({
            id: a.id,
            title: a.title,
            category_id: a.category_id,
            matchesFilter: selectedCategoryIds.includes(a.category_id),
          }))
        );

        // Kiểm tra có bài viết không
        if (filteredArticles.length === 0 && totalFilteredCount === 0) {
          setArticles([]);
          setHasMore(false);
          setLoading(false);
          return;
        }

        // Chuyển đổi sang dạng UI
        const categoriesArray: Category[] = categoriesData;
        const subcategoriesArray: Subcategory[] = subcategoriesData;

        const mappedArticles = filteredArticles.map((article) =>
          mapSupabaseArticleToUi(article, categoriesArray, subcategoriesArray)
        );

        setArticles(mappedArticles);

        // Kiểm tra xem còn bài viết để tải thêm không
        setHasMore(totalFilteredCount > mappedArticles.length);

        // Log để debug
        console.log(
          `Số bài viết phù hợp: ${totalFilteredCount}, Số bài đã tải: ${
            mappedArticles.length
          }, HasMore: ${totalFilteredCount > mappedArticles.length}`
        );

        setLoading(false);
      } catch (err) {
        console.error("Lỗi khi áp dụng bộ lọc:", err);
        setError("Đã xảy ra lỗi khi lọc dữ liệu. Vui lòng thử lại sau.");
        setLoading(false);
      }
    };

    applyFilters();
  }, [searchQuery, selectedCategories, selectedSubCategories]);

  // Hàm tải thêm bài viết
  const loadMoreArticles = async () => {
    if (!hasMore || loadingMore) return;

    setLoadingMore(true);

    try {
      // Tính toán trang tiếp theo
      const nextPage = page + 1;

      // Log debug information
      console.log("Loading more articles with filters:", {
        page: nextPage,
        selectedCategories,
        selectedSubCategories,
        categoriesMapping: categoriesData.map((c) => ({
          id: c.id,
          slug: c.slug,
        })),
        subcategoriesMapping: subcategoriesData.map((sc) => ({
          id: sc.id,
          slug: sc.slug,
          category_id: sc.category_id,
        })),
      });

      // Chuyển đổi slug thành ID
      const selectedCategoryIds = convertCategorySlugsToIds(selectedCategories);
      const selectedSubcategoryIds = convertSubcategorySlugsToIds(
        selectedSubCategories
      );

      // Lấy dữ liệu đã lọc từ API cho trang tiếp theo
      const { data: filteredArticles, count: totalFilteredCount } =
        await articlesService.getArticlesFiltered({
          page: nextPage,
          pageSize: itemsPerPage,
          categoryIds: selectedCategoryIds,
          subcategoryIds: selectedSubcategoryIds,
          searchQuery: searchQuery,
        });

      if (filteredArticles.length === 0) {
        setHasMore(false);
        setLoadingMore(false);
        return;
      }

      // Chuyển đổi dữ liệu
      const categoriesArray: Category[] = categoriesData;
      const subcategoriesArray: Subcategory[] = subcategoriesData;

      const mappedArticles = filteredArticles.map((article) =>
        mapSupabaseArticleToUi(article, categoriesArray, subcategoriesArray)
      );

      // Cập nhật state
      setArticles((prevArticles) => [...prevArticles, ...mappedArticles]);
      setPage(nextPage);

      // Cải thiện cách xác định còn dữ liệu hay không
      setHasMore(articles.length + mappedArticles.length < totalFilteredCount);

      setLoadingMore(false);
    } catch (err) {
      console.error("Lỗi khi tải thêm dữ liệu:", err);
      setLoadingMore(false);
    }
  };

  // Function to handle category selection
  const handleCategoryChange = (categorySlug: string, isChecked: boolean) => {
    console.log(
      `DEBUG - handleCategoryChange: ${categorySlug}, isChecked: ${isChecked}`
    );

    let newSelectedCategories;

    if (isChecked) {
      newSelectedCategories = [...selectedCategories, categorySlug];
    } else {
      newSelectedCategories = selectedCategories.filter(
        (slug) => slug !== categorySlug
      );

      // Also remove any subcategories of this category
      const subcatsToRemove = allSubcategories
        .filter((sc) => sc.category === categorySlug)
        .map((sc) => sc.slug);

      setSelectedSubCategories((prev) =>
        prev.filter((slug) => !subcatsToRemove.includes(slug))
      );
    }

    console.log("DEBUG - New selected categories:", newSelectedCategories);
    setSelectedCategories(newSelectedCategories);

    // Update URL params
    updateUrlParams(newSelectedCategories, selectedSubCategories);
  };

  // Function to handle subcategory selection
  const handleSubCategoryChange = (
    subCategorySlug: string,
    isChecked: boolean
  ) => {
    let newSelectedSubCategories;

    if (isChecked) {
      newSelectedSubCategories = [...selectedSubCategories, subCategorySlug];
    } else {
      newSelectedSubCategories = selectedSubCategories.filter(
        (slug) => slug !== subCategorySlug
      );
    }

    setSelectedSubCategories(newSelectedSubCategories);

    // Update URL params
    updateUrlParams(selectedCategories, newSelectedSubCategories);
  };

  // Update URL parameters
  const updateUrlParams = (categories: string[], subCategories: string[]) => {
    console.log("DEBUG - Updating URL params:", { categories, subCategories });
    const params: Record<string, string> = {};

    if (categories.length > 0) {
      params.category = categories.join(",");
    }

    if (subCategories.length > 0) {
      params.sub_category = subCategories.join(",");
    }

    console.log("DEBUG - New URL params:", params);
    setSearchParams(params);
  };

  // Handle search input
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
  };

  // Reset all filters
  const resetFilters = async () => {
    setSelectedCategories([]);
    setSelectedSubCategories([]);
    setSearchParams({});
    setSearchQuery("");

    // Đặt lại trang và hasMore
    setPage(1);

    // Tạm thời đánh dấu là đang tải
    setLoading(true);

    try {
      // Lấy bài viết không lọc từ API
      const { data: articlesData, count: totalCount } =
        await articlesService.getArticlesFiltered({
          page: 1,
          pageSize: itemsPerPage,
        });

      // Chuyển đổi dữ liệu
      const categoriesArray: Category[] = categoriesData;
      const subcategoriesArray: Subcategory[] = subcategoriesData;

      const mappedArticles = articlesData.map((article) =>
        mapSupabaseArticleToUi(article, categoriesArray, subcategoriesArray)
      );

      // Cập nhật state
      setArticles(mappedArticles);

      // Đảm bảo hasMore được đặt đúng
      setHasMore(totalCount > mappedArticles.length);

      setLoading(false);
    } catch (err) {
      console.error("Lỗi khi reset bộ lọc:", err);
      setLoading(false);
    }
  };

  // Toggle filter panel
  const toggleFilterPanel = () => {
    setIsFilterExpanded(!isFilterExpanded);
  };

  return {
    // Data
    articles,
    categories: categories.map((cat) => ({ id: cat.slug, name: cat.name })),
    subCategories: allSubcategories,
    categoryDisplayNames,
    availableSubcategories: getAvailableSubcategories(),

    // State
    loading,
    loadingMore,
    error,
    hasMore,
    searchQuery,
    selectedCategories,
    selectedSubCategories,
    isFilterExpanded,

    // Actions
    handleSearchChange,
    handleCategoryChange,
    handleSubCategoryChange,
    resetFilters,
    toggleFilterPanel,
    loadMoreArticles,
  };
}
